import React, { useEffect, useRef, useState } from 'react';
import { measureHostDelaySec } from './hlsDelay';
import { readFrameClockMs } from './frameClock';
import Hls from 'hls.js';
import { Box, CircularProgress, Typography } from '@mui/material';
import errorReporter from '../../services/error-reporter';

// A live stream may simply not be up yet, so network failures are retried
// with capped backoff; the RTC fallback only kicks in after a full minute
// without a single successfully loaded fragment.
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 15000;
const GIVE_UP_AFTER_MS = 60000;
const retryDelayMs = (attempt) => Math.min(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), RETRY_MAX_DELAY_MS);

const reportHlsFatal = (streamUrl, data) =>
  errorReporter.captureError({
    message: `HLS fatal error: ${data?.details || data?.type || 'unknown'}`,
    errorType: 'HLS',
    extra: { streamUrl, type: data?.type, details: data?.details, httpCode: data?.response?.code },
  });

/**
 * HLS Player Component
 * Low-latency HLS streaming with optimized buffering
 */
const DELAY_SAMPLE_MS = 2000;
const FRAME_CLOCK_SAMPLE_MS = 250;
// A stamped playlist still missing after this many tries is not coming (the
// stream predates the stamper); the plain playlist plays instead.
const CLOCK_URL_MISSING_LIMIT = 3;

const HLSPlayer = ({ 
  streamUrl, 
  isPlaying = true,
  onError,
  onReady,
  onMutedChange,
  onDelayChange,
  clockUrl = null,
  frameClockRef = null,
  style = {}
}) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  // The playlist in play: the stamped one when offered, the plain one if it is missing.
  const activeUrlRef = useRef(null);
  const clockUrlMissingRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Connecting to stream...');
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryCountRef = useRef(0); // Ref for use inside useEffect closure (state is stale there)
  const retryTimeoutRef = useRef(null);
  const firstFailureAtRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay compliance

  useEffect(() => {
    onMutedChange?.(isMuted);
  }, [isMuted, onMutedChange]);

  // How far this viewer runs behind the host, for players that follow the
  // host's timeline (the host's music on a direct OBS stream).
  useEffect(() => {
    if (!onDelayChange) return undefined;
    const timer = setInterval(() => {
      const delaySec = measureHostDelaySec(hlsRef.current, videoRef.current);
      if (delaySec !== null) onDelayChange(delaySec);
    }, DELAY_SAMPLE_MS);
    return () => clearInterval(timer);
  }, [onDelayChange]);

  // Wall-clock time of the frame on screen, for the host's music to follow.
  useEffect(() => {
    if (!frameClockRef) return undefined;
    const clock = frameClockRef.current;
    const timer = setInterval(() => {
      clock.ms = readFrameClockMs(hlsRef.current, videoRef.current);
    }, FRAME_CLOCK_SAMPLE_MS);
    return () => {
      clearInterval(timer);
      clock.ms = null;
    };
  }, [frameClockRef]);

  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    activeUrlRef.current = clockUrl || streamUrl;
    clockUrlMissingRef.current = 0;
    console.log('[HLSPlayer] Loading stream URL:', activeUrlRef.current);
    retryCountRef.current = 0;
    firstFailureAtRef.current = null;
    setRetryCount(0);

    // Check if HLS is supported
    if (Hls.isSupported()) {
      console.log('[HLSPlayer] Initializing HLS.js for:', streamUrl);

      // Create HLS instance — optimized for standard HLS (nginx-rtmp)
      // lowLatencyMode requires server-side LL-HLS (partial segments) which nginx-rtmp doesn't support
      const hls = new Hls({
        lowLatencyMode: false,
        // Hold 4 segments (8s at 2s fragments). Media Push hiccups arrive as
        // one-second gaps; a 4s buffer drained on every one of them.
        liveSyncDurationCount: 4,
        liveMaxLatencyDurationCount: 8,   // Max drift before seeking to edge
        liveDurationInfinity: true,
        enableWorker: true,
        backBufferLength: 5,
        // Loading settings
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 5,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 4,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 6,
        // Start from live edge
        startPosition: -1,
        // Buffer — reduced from defaults but not starving
        maxBufferLength: 15,
        maxMaxBufferLength: 30,
        maxBufferSize: 30 * 1000 * 1000,
        maxBufferHole: 0.1,
        // Audio codec hint
        audioCodec: 'mp4a.40.2',
        // XHR setup
        xhrSetup: function(xhr, url) {
          xhr.withCredentials = false;
        },
        // Cache-bust playlist URLs only (segments have unique names already)
        pLoader: class extends Hls.DefaultConfig.loader {
          load(context, config, callbacks) {
            if (context.url && context.url.includes('.m3u8')) {
              const separator = context.url.includes('?') ? '&' : '?';
              context.url = `${context.url}${separator}_t=${Date.now()}`;
            }
            return super.load(context, config, callbacks);
          }
        }
      });
      
      hlsRef.current = hls;
      
      // Load the stream with cache-busted URL
      hls.loadSource(activeUrlRef.current);
      hls.attachMedia(videoRef.current);
      
      // Handle HLS events
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('[HLSPlayer] Manifest loaded, starting playback');
        setLoadingMessage('Stream ready, starting playback...');
        if (isPlaying && videoRef.current) {
          // Try unmuted first, fall back to muted autoplay
          videoRef.current.muted = false;
          videoRef.current.play()
            .then(() => {
              console.log('[HLSPlayer] Playback started with audio');
              setIsMuted(false);
              setIsLoading(false);
              setHasError(false);
              retryCountRef.current = 0;
              setRetryCount(0);
              if (onReady) onReady();
            })
            .catch(() => {
              // Autoplay with audio blocked — retry muted
              console.log('[HLSPlayer] Unmuted autoplay blocked, retrying muted');
              videoRef.current.muted = true;
              setIsMuted(true);
              videoRef.current.play()
                .then(() => {
                  console.log('[HLSPlayer] Playback started muted');
                  setIsLoading(false);
                  setHasError(false);
                  retryCountRef.current = 0;
                  setRetryCount(0);
                  if (onReady) onReady();
                })
                .catch(e => {
                  console.error('[HLSPlayer] Playback failed entirely:', e);
                  setLoadingMessage('Click to start playback');
                  setIsLoading(false);
                  if (onReady) onReady();
                });
            });
        }
      });
      
      // Handle when video starts playing
      hls.on(Hls.Events.FRAG_LOADED, () => {
        if (isLoading) {
          console.log('[HLSPlayer] First fragment loaded');
          setLoadingMessage('Buffering stream...');
        }
        firstFailureAtRef.current = null;
        if (retryCountRef.current > 0) {
          retryCountRef.current = 0;
          setRetryCount(0);
        }
      });

      const giveUp = (data, message) => {
        reportHlsFatal(streamUrl, data);
        setHasError(true);
        setLoadingMessage(message);
        if (onError) onError(data);
      };

      const scheduleRetry = (data, message, retry) => {
        if (!firstFailureAtRef.current) firstFailureAtRef.current = Date.now();
        if (Date.now() - firstFailureAtRef.current > GIVE_UP_AFTER_MS) {
          giveUp(data, 'Unable to connect to stream. Please try refreshing.');
          return;
        }
        retryCountRef.current += 1;
        setRetryCount(retryCountRef.current);
        setLoadingMessage(message);
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(retry, retryDelayMs(retryCountRef.current));
      };
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('[HLSPlayer] HLS error:', data);
        
        // Handle 404 errors (stream not ready yet)
        if (data.details === 'manifestLoadError' && data.response?.code === 404) {
          if (activeUrlRef.current !== streamUrl && ++clockUrlMissingRef.current >= CLOCK_URL_MISSING_LIMIT) {
            console.warn('[HLSPlayer] Stamped playlist missing; playing the plain one');
            activeUrlRef.current = streamUrl;
            hls.loadSource(streamUrl);
            hls.attachMedia(videoRef.current);
            return;
          }
          console.log(`[HLSPlayer] Stream not ready (404), retry ${retryCountRef.current + 1}`);
          scheduleRetry(data, 'Waiting for host to start streaming...', () => {
            console.log('[HLSPlayer] Retrying to load stream...');
            hls.loadSource(activeUrlRef.current);
            hls.attachMedia(videoRef.current);
          });
          return;
        }
        
        // Handle buffer stalled errors specially
        if (data.details === 'bufferStalledError') {
          console.log('[HLSPlayer] Buffer stalled, attempting recovery');
          setLoadingMessage('Buffering stream...');
          
          // Try to nudge playback forward slightly
          if (videoRef.current) {
            const currentTime = videoRef.current.currentTime;
            const buffered = videoRef.current.buffered;
            
            if (buffered.length > 0) {
              const bufferedEnd = buffered.end(buffered.length - 1);
              if (bufferedEnd > currentTime + 0.1) {
                videoRef.current.currentTime = currentTime + 0.1;
              }
            }
          }
          return; // Don't treat as fatal
        }
        
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('[HLSPlayer] Fatal network error, trying to recover');
              
              if (data.details === 'manifestLoadError') {
                scheduleRetry(data, 'Waiting for stream to start...', () => {
                  console.log('[HLSPlayer] Retrying to load manifest...');
                  hls.loadSource(activeUrlRef.current);
                  hls.startLoad();
                });
                break;
              }

              scheduleRetry(data, 'Network error, reconnecting...', () => {
                console.log('[HLSPlayer] Retrying load after network error');
                hls.startLoad();
              });
              break;
              
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('[HLSPlayer] Fatal media error, trying to recover');
              setLoadingMessage('Media error, recovering...');
              hls.recoverMediaError();
              break;
              
            default:
              console.error('[HLSPlayer] Fatal error, cannot recover');
              giveUp(data, 'Stream error. Please try refreshing.');
              break;
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          // Non-fatal media errors - try recovery
          console.log('[HLSPlayer] Non-fatal media error, recovering');
          hls.recoverMediaError();
        } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          // Non-fatal network errors - show loading state
          setLoadingMessage('Buffering...');
        }
      });
      
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari, iOS)
      console.log('[HLSPlayer] Using native HLS support');
      setLoadingMessage('Loading stream (native)...');
      
      // Add event listeners for native video
      const handleCanPlay = () => {
        console.log('[HLSPlayer] Native video can play');
        setLoadingMessage('Stream ready...');
      };
      
      const handlePlaying = () => {
        console.log('[HLSPlayer] Native video playing');
        setIsLoading(false);
        setHasError(false);
        if (onReady) onReady();
      };
      
      const handleWaiting = () => {
        console.log('[HLSPlayer] Native video waiting');
        setLoadingMessage('Buffering...');
        setIsLoading(true);
      };
      
      const handleError = (e) => {
        if (activeUrlRef.current !== streamUrl) {
          console.warn('[HLSPlayer] Stamped playlist failed natively; playing the plain one');
          activeUrlRef.current = streamUrl;
          videoRef.current.src = streamUrl;
          if (isPlaying) videoRef.current.play().catch(() => {});
          return;
        }
        console.error('[HLSPlayer] Native video error:', e);
        setHasError(true);
        setLoadingMessage('Stream error. Please try refreshing.');
        if (onError) onError(e);
      };
      
      videoRef.current.addEventListener('canplay', handleCanPlay);
      videoRef.current.addEventListener('playing', handlePlaying);
      videoRef.current.addEventListener('waiting', handleWaiting);
      videoRef.current.addEventListener('error', handleError);
      
      // Add cache-busting for native support too
      videoRef.current.src = activeUrlRef.current;
      if (isPlaying) {
        videoRef.current.play().catch(e => {
          console.error('[HLSPlayer] Native playback failed:', e);
          setLoadingMessage('Click to start playback');
          setIsLoading(false);
        });
      }
      
      // Cleanup native event listeners
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('canplay', handleCanPlay);
          videoRef.current.removeEventListener('playing', handlePlaying);
          videoRef.current.removeEventListener('waiting', handleWaiting);
          videoRef.current.removeEventListener('error', handleError);
        }
      };
    } else {
      console.error('[HLSPlayer] HLS not supported in this browser');
      if (onError) onError(new Error('HLS not supported'));
    }

    // Cleanup
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (hlsRef.current) {
        console.log('[HLSPlayer] Cleaning up HLS instance');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl, clockUrl]); // Only re-initialize when the playlist changes

  // Handle play/pause
  useEffect(() => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.play().catch(e => {
        console.error('[HLSPlayer] Play failed:', e);
      });
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: 'black',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
    >
      <video
        ref={videoRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: isLoading ? 'none' : 'block',
          cursor: isMuted ? 'pointer' : 'default'
        }}
        controls={false}
        playsInline
        muted={isMuted}
        onClick={() => {
          if (isMuted && videoRef.current) {
            videoRef.current.muted = false;
            setIsMuted(false);
          }
        }}
      />

      {/* Unmute prompt */}
      {!isLoading && isMuted && (
        <Box
          onClick={() => {
            if (videoRef.current) {
              videoRef.current.muted = false;
              setIsMuted(false);
            }
          }}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: 'pointer',
            zIndex: 20,
            fontSize: '14px',
            userSelect: 'none',
          }}
        >
          🔇 Click to unmute
        </Box>
      )}
      
      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 10
          }}
        >
          {!hasError ? (
            <>
              <CircularProgress 
                size={60} 
                sx={{ 
                  color: '#fff',
                  mb: 3
                }} 
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#fff',
                  textAlign: 'center',
                  px: 2
                }}
              >
                {loadingMessage}
              </Typography>
              {retryCount > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#888',
                    mt: 1
                  }}
                >
                  Retry attempt {retryCount}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#fff',
                  textAlign: 'center',
                  px: 2
                }}
              >
                {loadingMessage}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#888',
                  mt: 2,
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => window.location.reload()}
              >
                Click here to refresh
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(HLSPlayer, (prevProps, nextProps) => {
  // Only re-render if streamUrl or isPlaying changes
  return prevProps.streamUrl === nextProps.streamUrl && 
         prevProps.isPlaying === nextProps.isPlaying;
});